import { Popup } from '@progress/kendo-react-popup'
import AlertPrompt from 'components/AlertPrompt'
import { ClientCombobox } from 'components/BookingPageComponents/ClientCombobox'
import { MembershipSelect } from 'components/BookingPageComponents/MembershipComponent'
import { StaffAvailability } from 'components/DropdownSelectors/common'
import Translation from 'components/Translation'
import { useAppointmentDrawerContext } from 'context/appointmentDrawerContext/AppointmentDrawerContainer'
import { useTenantContext } from 'context/TenantContext'
import React, { useEffect, useState } from 'react'
import {
    ClientMembershipItem,
    MembershipClientOutput,
} from 'types/client-membership'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest, useFetch } from 'utils/fetch-helper'
import { validateEmail } from 'utils/string-helper'
import { addDurationToStrDate, strToDate, toHHMM12, toYmd } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import CouponValidity from './CouponValidity'
import CustomSwitch from './CustomSwitch'
import { useAppContext } from 'context/AppContext'
import RecurringBookings, { generateRecurringOrderBookingsDTO } from 'components/RecurringBookings'
import { ServiceApiTypeEnum, ServiceItem } from 'types/service'
import { BookingDTO, OrderBookingWithoutBookingDTO, OrderDTO, RecurringBookingDates } from 'types/order-booking'
import { OrderType } from 'types/order'
import { DiscountType } from 'types/discount'

function AddAttendeeModal() {
    const { tenant, services} = useTenantContext()
    const { setGlobalLoading, translations } = useAppContext()
    const {
        selectedClient,
        shouldApplyCouponCode,
        setShouldApplyCouponCode,
        applyFreeAppointment,
        setApplyFreeAppointment,
        setSelectedClient,
        setDisplayAddAttendeeModal,
        setCouponCode,
        formData: { location, service, staff, space, time, date },
        couponCode,
        isValidCouponCode,
        handleAddAttendeeSubmit,
        selectedMembership,
        displayNewClientForm,
        setSelectedMembership,
        setDisplayNewClientForm,
        err,
        setErrMsg,
        setIsSuccess,
        availabilityStatus,
        couponInfo,
        displayAvailabilityModal,
        setDisplayAvailabilityModal,
        setIsValidCouponCode,
        newClientInfo,
        setNewClientInfo,
    } = useAppointmentDrawerContext()
    const [membership_options, setMembershipOptions] = useState<
        ClientMembershipItem[]
    >([])
    const [selected_membership, selectMembership] =
        useState<ClientMembershipItem>()
    const [popup_anim_css, setPopAmin] = useState<string>('bg-opacity-50')
    const [is_recurring, toggleRecurring] = useState<boolean>(false)
    const [recurring_dates, setRecurringDates] = useState<RecurringBookingDates[]>([])
    const [valid_recurring_count, setValidRecurringCount] = useState<number>(0)
    const [is_editing, toggleEditing] = useState<boolean>(false)
    const [service_item, setServiceItem] = useState<ServiceItem | undefined>()

    const { is_loading, data } = useFetch(
        `/v1/memberships/clients/by-service?clientEmail=${encodeURIComponent(
            selectedClient?.user.email as string
        )}&serviceId=${service}&tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!selectedClient &&
        !!service &&
        validateEmail(selectedClient.user.email) &&
        newClientInfo === undefined
    )

    useEffect(() => {
        setServiceItem(services?.find((s) => s.id == service))
    }, [service, services])

    useEffect(() => {
        if (!applyFreeAppointment && !shouldApplyCouponCode) {
            setSelectedMembership(membership_options[0]?.id)
            setCouponCode('')
        }
        if (applyFreeAppointment) {
            setShouldApplyCouponCode(false)
            setSelectedMembership(undefined)
            setCouponCode('')
        }
        if (shouldApplyCouponCode) {
            setApplyFreeAppointment(false)
            setSelectedMembership(undefined)
        }
    }, [applyFreeAppointment, shouldApplyCouponCode])

    useEffect(() => {
        if (!is_loading && !!data && !!data.length) {
            if (!data || data.length === 0) setMembershipOptions([])
            else {
                const memberships = data.map(
                    (mc: MembershipClientOutput) => {
                        const { membership, membershipClient } = mc
                        const client_membership: ClientMembershipItem = {
                            id: membershipClient.id,
                            membership: {
                                id: membership.id as string,
                                name: membership.name,
                                img: membership?.imageUrl as string,
                            },
                            client: {
                                id: membershipClient.clientId,
                                used: {
                                    services:
                                        membershipClient.usedServiceCount,
                                    minutes: membershipClient.usedMinutes,
                                    amount: membershipClient.usedAmount,
                                },
                                starts_at: membershipClient.startsAt,
                                expires_at: membershipClient.expiresAt,
                                renews_at: membershipClient.renewsAt,
                            },
                            service_count: mc.availableServiceCount,
                            minute_count: mc.availableMinutes,
                            amount: mc.availableAmount,
                            unlimited: mc.redemptionUnlimited,
                            entitled: mc.entitled,
                            not_entitled_reason: mc.notEntitledReason,
                            content_tag: mc.contentTag,
                            member_price: mc.memberPrice,
                            member_price_type: mc.memberPriceType,
                            service_price: mc.servicePrice,
                            service_full_price: mc.serviceFullPrice,
                            discount_amount: mc.discountAmount,
                        }
                        return client_membership
                    }
                )
                setMembershipOptions(memberships)
            }
        }
    }, [data, is_loading])

    const closeCurrentModal = () => {
        setDisplayAddAttendeeModal(false)
        setSelectedClient(undefined)
        setCouponCode('')
        setApplyFreeAppointment(false)
        setShouldApplyCouponCode(false)
    }

    const generatePayload = (proceed_any_way = false) => {
        let payload: any = {}
        const today = new Date()
        const splittedTime = time.split(':')
        let modifiedTime = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            +splittedTime[0],
            +splittedTime[1],
            0
        )

        //if coupon code is available or free appointment or membership is Selected
        if (
            isValidCouponCode ||
            applyFreeAppointment ||
            selectedMembership
        ) {
            payload = {
                amount: +(
                    services?.find((item) => item.id === service)?.price ||
                    0
                ),
                tenantId: tenant?.id,
                locationId: location,
                clientEmail: selectedClient?.user.email,
                order: {
                    amount: +(
                        services.find((item) => item.id === service)
                            ?.price || 0
                    ),
                    clientEmail: selectedClient?.user.email,
                    clientFirstName: selectedClient?.user.first_name,
                    clientLastName: selectedClient?.user.last_name,
                    clientPhone: selectedClient?.user.phone,
                    tenantId: tenant?.id,
                    type: 'SERVICE',
                    couponCode: couponCode,
                    fullyPaid: false,
                },
                booking: {
                    appointmentDto: {
                        date: date,
                        startTime: toHHMM12(modifiedTime),
                        endTime: toHHMM12(modifiedTime),
                        serviceId: service,
                        locationId: location,
                        locationSpaceId: space,
                        staffId: staff,
                    },
                    clientEmail: selectedClient?.user.email,
                    tenantId: tenant?.id,
                },
                url: `/v1/orders/order-booking?sendNotification=true&allowOverlap=${proceed_any_way}`,
                type: 'FREE',
            }
            if (applyFreeAppointment) {
                payload = {
                    ...payload,
                    amount: 0,
                    order: {
                        ...payload?.order,
                        adhocDiscount: 100,
                        adhocDiscountType: 'PERCENT',
                    },
                }
            }
            if (selectedMembership) {
                payload = {
                    ...payload,
                    amount: 0,
                    order: {
                        ...payload.order,
                        membershipClientId: selectedMembership,
                    },
                }
            }
            if (isValidCouponCode) {
                payload = {
                    ...payload,
                    amount: calculateAmountWithCoupon(
                        +(
                            services.find((item) => item.id === service)
                                ?.price || 0
                        )
                    ),
                    order: {
                        ...payload.order,
                        paymentMethod: 'CASH',
                        paymentAmount: calculateAmountWithCoupon(
                            +(
                                services.find((item) => item.id === service)
                                    ?.price || 0
                            )
                        ),
                        amount: calculateAmountWithCoupon(
                            +(
                                services.find((item) => item.id === service)
                                    ?.price || 0
                            )
                        ),
                    },
                }

                if (payload.amount === 0) {
                    delete payload.order.paymentMethod
                }
            }
        }

        return payload
    }

    const calculateAmountWithCoupon = (price: number) => {
        let finalAmount = 0

        switch (couponInfo?.discountType) {
            case 'EXACT_VALUE':
                finalAmount =
                    price > couponInfo?.discountValue
                        ? price - couponInfo?.discountValue
                        : price
                break
            case 'PERCENT':
                finalAmount =
                    price - (couponInfo?.discountValue * price) / 100
                break
            default:
                finalAmount = price
        }

        return finalAmount
    }

    const handleAddAttendee = () => {
        switch (availabilityStatus) {
            case StaffAvailability.AVAILABLE:
                saveHandler()
                break
            case StaffAvailability.BUSY:
                setDisplayAvailabilityModal(true)
                break
            case StaffAvailability.NO_AVAILABILITY:
                setDisplayAvailabilityModal(true)
                break
            default:
                undefined
        }
    }

    async function saveHandler(proceed_any_way = false) {
        setDisplayAvailabilityModal(false)
        setGlobalLoading(true)
        try {
            let res
            if (is_recurring) {
                const amount_after_discount = calculateAmountWithCoupon(service_item?.price || 0)
                const has_discount = (applyFreeAppointment || (isValidCouponCode && couponInfo && amount_after_discount == 0))
                res = await postApiRequest(`/v1/orders/order-booking-from-list?allowOverlap=${proceed_any_way}`, generateRecurringOrderBookingsDTO({
                    amount: (applyFreeAppointment
                        || selectedMembership)
                        ? 0
                        : (amount_after_discount) * (valid_recurring_count),
                    order: {
                        clientEmail: selectedClient?.user.email,
                        clientFirstName: selectedClient?.user.first_name,
                        clientLastName: selectedClient?.user.last_name,
                        clientPhone: selectedClient?.user.phone,
                        tenantId: tenant?.id,
                        type: OrderType.SERVICE,
                        couponCode: couponCode,
                        fullyPaid: false,
                        membershipClientId: selected_membership?.id || undefined,
                        adhocDiscount: has_discount ? 100 : undefined,
                        adhocDiscountType: has_discount ? isValidCouponCode && couponInfo ? couponInfo.discountType : DiscountType.PERCENT : undefined
                    } as OrderDTO,
                } as OrderBookingWithoutBookingDTO,
                    {
                        appointmentDto: {
                            date: toYmd(new Date(date)),
                            startTime: toHHMM12(strToDate(new Date(date), time)),
                            endTime: toHHMM12(addDurationToStrDate(new Date(date), time, service_item?.duration || 0)),
                            serviceId: service,
                            locationId: location,
                            tenantId: tenant?.id,
                            staffId: staff,
                        },
                        clientEmail: selectedClient?.user.email,
                        clientFirstName: selectedClient?.user.first_name,
                        clientLastName: selectedClient?.user.last_name,
                        clientPhone: selectedClient?.user.phone,
                        tenantId: tenant?.id,
                    } as BookingDTO, recurring_dates || [], service_item?.duration || 0, undefined, true, staff))
            } else {
                const payload = generatePayload(proceed_any_way)
                res = await handleAddAttendeeSubmit({ ...payload })
            }

            if (res?.ok) {
                setIsSuccess(true)
            } else {
                setErrMsg(res.message || 'ERROR!')
            }
            setDisplayAvailabilityModal(false)
        } catch (err) {
            setErrMsg(JSON.stringify(err))
        }
        setGlobalLoading(false)
    }

    const shouldDisableSaveButton = () => {
        let indicator = false

        if (is_recurring && valid_recurring_count < 1) {
            return true
        }

        if (!selected_membership) {
            indicator = true
        }
        if (!isValidCouponCode && shouldApplyCouponCode) {
            indicator = true
        }
        if (isValidCouponCode && shouldApplyCouponCode) {
            indicator = false
        }
        if (applyFreeAppointment) {
            indicator = false
        }
        if (selectedMembership) {
            indicator = false
        }
        if (!selectedClient) indicator = true
        return indicator
    }

    return (
        <div
            className="border-gray-200 rounded-md border p-3 relative"
            id="addAttendeeModalId"
        >
            <span
                className="absolute right-2 top-0 cursor-pointer"
                onClick={closeCurrentModal}
            >
                x
            </span>
            <div>
                <div className="mt-2 mb-10">
                    <h3
                        className={`${'text-black'} leading-none whitespace-pre-line mx-auto text-center`}
                    >
                        <Translation
                            content_key={'add_attendee'}
                            render_as="span"
                            className="font-semibold text-lg"
                        />
                        <small className="block">
                            <Translation content_key="search_client" />
                        </small>
                    </h3>
                </div>
            </div>
            <div>
                {!displayNewClientForm ? (
                    newClientInfo?.user ? (
                        <div className="flex justify-between">
                            <span className="bg-green-200 px-5 py-2 rounded-md">
                                {newClientInfo.user.first_name} &nbsp;
                                {newClientInfo.user.last_name}
                                <span
                                    className="ml-3 bg-red-500 text-white px-2 rounded-md cursor-pointer"
                                    onClick={() => {
                                        setNewClientInfo(undefined)
                                        setSelectedClient(undefined)
                                        setMembershipOptions([])
                                    }}
                                >
                                    x
                                </span>
                            </span>
                        </div>
                    ) : (
                        <>
                            <ClientCombobox
                                onSelect={(client) =>
                                    setSelectedClient(client)
                                }
                                tenant_id={tenant?.id as string}
                            />
                            <button
                                className="px-7 py-2 border border-gray-200 rounded-md mt-2"
                                onClick={() => {
                                    setDisplayNewClientForm(true)
                                    setSelectedClient(undefined)
                                }}
                            >
                                <Translation content_key="new_client" />
                            </button>
                        </>
                    )
                ) : (
                    selectedClient && 'New Client Added...'
                )}
                <div className="mt-3">
                    {membership_options.length > 0 &&
                        !applyFreeAppointment &&
                        !shouldApplyCouponCode && (
                            <div className="mt-4">
                                <MembershipSelect
                                    options={membership_options}
                                    onSelect={(membership) => {
                                        setSelectedMembership(
                                            membership?.id
                                        )
                                    }}
                                    key={'selectMembership'}
                                />
                            </div>
                        )}
                </div>
                {!!selectedClient && (
                    <>
                        <div className="mt-4">
                            <div>
                                <CustomSwitch
                                    value={applyFreeAppointment}
                                    onChangeValue={(val) => {
                                        setApplyFreeAppointment(val)
                                    }}
                                    label={
                                        translations[
                                        'set_free_appointment'
                                        ] || 'Set as a free appointment'
                                    }
                                />
                            </div>
                            {((isValidCouponCode && calculateAmountWithCoupon(service_item?.price || 0)) ||
                                applyFreeAppointment ||
                                selectedMembership)
                                &&
                                <div className='mt-2'>
                                    <RecurringBookings
                                        primary_color='#4b95b7'
                                        start={strToDate(new Date(date), time)}
                                        end={addDurationToStrDate(new Date(date), time, service_item?.duration || 0)}
                                        is_recurring={is_recurring}
                                        toggleRecurring={toggleRecurring}
                                        recurring_dates={recurring_dates}
                                        setRecurringDates={setRecurringDates}
                                        service_id={service}
                                        service_type={ServiceApiTypeEnum.APPOINTMENT}
                                        location_id={location}
                                        is_editing={is_editing}
                                        toggleEditing={toggleEditing}
                                        grid_count={1}
                                        valid_recurring_count={valid_recurring_count}
                                        setValidRecurringCount={setValidRecurringCount}
                                        membership_client={selected_membership}
                                    />
                                </div>
                            }
                            <div>
                                <CustomSwitch
                                    value={shouldApplyCouponCode}
                                    onChangeValue={(val) => {
                                        setShouldApplyCouponCode(val)
                                    }}
                                    label={
                                        translations['apply_coupon_code'] ||
                                        'Apply coupon code'
                                    }
                                />
                            </div>
                        </div>
                        <div className="mt-3">
                            {shouldApplyCouponCode && <CouponValidity />}
                        </div>
                    </>
                )}
                {/* <div className="mt-3">{renderMembershipsSelection()}</div> */}
                <div className="flex justify-end mt-4">
                    <button
                        className="px-7 py-2 border-gray-200 border rounded-md mr-2"
                        onClick={closeCurrentModal}
                    >
                        <Translation content_key="cancel" />
                    </button>{' '}
                    <button
                        disabled={shouldDisableSaveButton()}
                        className="px-7 py-2 border text-white rounded-md bg-primary border-primary disabled:bg-gray-400 disabled:border-gray-400 disabled:cursor-not-allowed"
                        onClick={() => handleAddAttendee()}
                    >
                        <Translation content_key="book_appointment" />
                    </button>
                </div>
            </div>
            {displayAvailabilityModal && (
                <Popup
                    show={displayAvailabilityModal}
                    popupAlign={{
                        horizontal: 'right',
                        vertical: 'top',
                    }}
                    animate={false}
                    popupClass={classNames(
                        'bg-black h-screen transition ease-in-out duration-200 flex flex-row items-center justify-between',
                        popup_anim_css
                    )}
                    className="z-[102]"
                    style={{
                        width: '100%',
                    }}
                >
                    <div className="flex-1">
                        <AlertPrompt
                            show={displayAvailabilityModal}
                            title={
                                availabilityStatus ===
                                    StaffAvailability.BUSY
                                    ? 'availability_busy'
                                    : availabilityStatus ===
                                        StaffAvailability.NO_AVAILABILITY
                                        ? 'availability_not_available'
                                        : ''
                            }
                            body={
                                <div className="text-lg">
                                    {availabilityStatus ===
                                        StaffAvailability.NO_AVAILABILITY && (
                                            <Translation content_key="avalibility_not_available_prompt" />
                                        )}
                                </div>
                            }
                            icon="alert-triangle bg-yellow-500"
                            actions={[
                                <button
                                    className="px-7 py-3 border border-gray-200 rounded-md "
                                    key={'cancel'}
                                    onClick={() => {
                                        setDisplayAvailabilityModal(false)
                                    }}
                                >
                                    <Translation content_key="okay_go_back" />
                                </button>,
                                availabilityStatus !==
                                    StaffAvailability.BUSY ? (
                                    <button
                                        onClick={() => {
                                            saveHandler()
                                        }}
                                        key={'proceed'}
                                        className="border b-2 border-primary bg-primary rounded-md px-7 py-3 text-white"
                                    >
                                        <Translation content_key="proceed" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => {
                                            saveHandler(true)
                                        }}
                                        key={'proceed_any_way'}
                                        className="border b-2 border-primary bg-primary rounded-md px-7 py-3 text-white"
                                    >
                                        <Translation content_key="proceed_any_way" />
                                    </button>
                                ),
                            ]}
                        />
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default AddAttendeeModal
